import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";

/**
 * Gross for an invoice row. Paid invoices contribute to paid; everything else is outstanding.
 * @param {object} invoice
 * @returns {number}
 */
function invoiceGross(invoice) {
  if (!invoice) return 0;
  if (invoice.total != null && invoice.total !== 0) return Number(invoice.total) || 0;
  return Number(invoice.calculatedTotalCharges) || Number(invoice.total) || 0;
}

/**
 * MultiClientSummaryTable
 * One row per client with invoice count, gross, paid, and outstanding.
 * Rendered into the invoice report PDF when more than one client is in the result.
 *
 * @param {object} props
 * @param {Array} props.data - Invoice report rows
 */
const MultiClientSummaryTable = ({ data = [] }) => {
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const clientTotals = React.useMemo(() => {
    const grouped = {};

    data.forEach((item) => {
      const client = item.client;
      const clientId =
        client?._id?.toString() ||
        (client && typeof client !== "object" ? String(client) : "Unknown");
      const clientName = client?.name || client?.fullName || "Unknown client";

      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientId,
          clientName,
          count: 0,
          gross: 0,
          paid: 0,
          outstanding: 0,
        };
      }

      const gross = invoiceGross(item);
      grouped[clientId].count += 1;
      grouped[clientId].gross += gross;
      if (item.status === "Paid") {
        grouped[clientId].paid += gross;
      } else {
        grouped[clientId].outstanding += gross;
      }
    });

    return Object.values(grouped).sort((a, b) =>
      a.clientName.localeCompare(b.clientName)
    );
  }, [data]);

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "clientName",
        header: "Client",
      },
      {
        accessorKey: "count",
        header: "Invoices",
      },
      {
        accessorKey: "gross",
        header: "Gross",
        cell: ({ getValue }) => formatCurrency(getValue()),
      },
      {
        accessorKey: "paid",
        header: "Paid",
        cell: ({ getValue }) => formatCurrency(getValue()),
      },
      {
        accessorKey: "outstanding",
        header: "Outstanding",
        cell: ({ getValue }) => formatCurrency(getValue()),
      },
    ],
    []
  );

  const table = useReactTable({
    data: clientTotals,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const thClasses = classNames(
    "border-b-2 py-2 text-xs text-left px-1 font-bold"
  );
  const tdClasses = classNames("py-1 text-sm text-left px-1");

  if (clientTotals.length === 0) {
    return null;
  }

  return (
    <table id="multi-client-totals">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className={thClasses}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className={tdClasses}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default MultiClientSummaryTable;
